import type { FeedbackStatus, FeedbackPost, FeedbackCategory } from './api';

/**
 * Vad varje läge HETER och vad det BETYDER för den som skrev.
 *
 * Statusnamn utan förklaring blir gissningar: "Mottagen" kan lika gärna
 * betyda "vi har läst" som "vi har börjat". Texterna nedan är vad vi
 * faktiskt lovar, och de skrivs ut i vyn — inte bara etiketten.
 */
export const STATUS_ETIKETT: Record<FeedbackStatus, string> = {
  ny: 'Inskickad',
  mottagen: 'Mottagen',
  pagar: 'Pågår',
  vantar_kund: 'Väntar på dig',
  klar: 'Klar',
  avvisad: 'Avvisad',
};

export const STATUS_INNEBORD: Record<FeedbackStatus, string> = {
  ny: 'Inskickad. Vi har ännu inte tittat på den.',
  mottagen: 'Vi har läst den och håller med om att den behöver hanteras.',
  pagar: 'Arbetet är påbörjat.',
  vantar_kund: 'Vi har ställt en fråga och väntar på ditt svar innan vi kan gå vidare.',
  klar: 'Åtgärdat och ute i systemet.',
  avvisad: 'Vi gör inte det här — läs svaret för varför.',
};

export const KATEGORI_ETIKETT: Record<FeedbackCategory, string> = {
  forbattring: 'Förbättringsförslag',
  bugg: 'Bugg / problem',
  fraga: 'Fråga',
  annat: 'Annat',
};

/** Har vi skrivit något, eller bara flyttat en status? */
export function harSvar(p: Pick<FeedbackPost, 'svar'>): boolean {
  return Boolean(p.svar && p.svar.trim());
}

/**
 * Väntar posten på oss?
 *
 * `avvisad` räknas som besvarad: vi HAR tagit ställning. Att låta ett nej
 * ligga kvar som obesvarat gör att listan aldrig blir tom och därmed slutar
 * betyda något.
 */
export function vantarPaOss(p: Pick<FeedbackPost, 'status'>): boolean {
  return p.status === 'ny' || p.status === 'mottagen' || p.status === 'pagar';
}

/**
 * Ett nej UTAN motivering är det sämsta utfallet: avsändaren ser att vi sagt
 * nej men inte varför, och nästa gång skriver hen inte alls.
 */
export function avvisadUtanMotivering(p: Pick<FeedbackPost, 'status' | 'svar'>): boolean {
  return p.status === 'avvisad' && !harSvar(p);
}

// `vantar_kund` ligger ÖVERST, före det obesvarade.
//
// Listan sorterar annars efter vad som väntar på oss, vilket är rätt när man
// frågar sig vad Samify har att göra. Men den som LÄSER listan är användaren,
// och en post där vi väntar på deras svar är den enda de själva kan flytta
// framåt. Ligger den under tre pågående hittas den aldrig, och ärendet står
// stilla för att bägge parter väntar på varandra.
const ORDNING: Record<FeedbackStatus, number> = {
  vantar_kund: 0,
  ny: 1,
  mottagen: 2,
  pagar: 3,
  klar: 4,
  avvisad: 5,
};

/**
 * Listordningen: det som kräver handling först, sedan nyast inom varje läge.
 *
 * Sorterar en KOPIA — `Array.prototype.sort` muterar, och listan kommer ur
 * komponentens state.
 */
export function sortera<T extends Pick<FeedbackPost, 'status' | 'createdAt'>>(
  poster: readonly T[],
): T[] {
  return [...poster].sort((a, b) => {
    const lage = ORDNING[a.status] - ORDNING[b.status];
    if (lage !== 0) return lage;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/**
 * Datum med ÅRTAL bara när det avviker från innevarande år.
 *
 * Årtalet är inte prydnad: listan sorterar efter läge, så en post från maj
 * kan ligga överst i december — och "9 maj 09:12" läses då som i år.
 *
 * `idag` skickas in i stället för att läsas ur `new Date()`, annars går
 * funktionen inte att verifiera och en formatering som byter utseende vid
 * nyår är inget man vill upptäcka i drift.
 */
export function datumtext(stamp: string, idag: Date): string {
  const d = new Date(stamp);
  const visaAr = d.getFullYear() !== idag.getFullYear();
  return d.toLocaleString('sv-SE', {
    day: 'numeric',
    month: 'short',
    ...(visaAr ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Raden under varje post.
 *
 * ⚠️ Läst-stämpeln ERSÄTTER statustexten, den staplas inte på den. Annars
 * står det "Vi har ännu inte tittat på den. Samify öppnade den 16:13." i
 * samma mening — två påståenden som säger emot varandra, och läsaren får
 * gissa vilket som gäller.
 */
export function statusrad(p: FeedbackPost, idag: Date): string {
  // ⚠️ Bara läget 'ny'.
  //
  // Stämpeln finns för att ersätta EN mening som blir osann när vi öppnat
  // posten: "Inskickad. Vi har ännu inte tittat på den." Alla andra lägen
  // säger redan något mer precist, och att skriva över dem med "inget svar
  // än" gör beskedet sämre:
  //
  //   pagar       "Arbetet är påbörjat" → mer informativt än att vi tittat
  //   vantar_kund "Vi väntar på ditt svar" → stämpeln hade dolt att bollen
  //               ligger hos DEM, vilket är radens hela syfte
  //   klar        arbetet är gjort; "inget svar än" vore rent felaktigt
  //   avvisad     "läs svaret för varför" — och saknas svaret flaggas det
  //               separat som ett nej utan motivering
  if (p.status === 'ny' && p.seddAvSamifyAt && !harSvar(p)) {
    return `Samify öppnade den ${datumtext(p.seddAvSamifyAt, idag)}. Inget svar än.`;
  }
  return STATUS_INNEBORD[p.status];
}
